import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, combineLatest, map } from 'rxjs';
import { environment } from 'src/environments';
import {
  CreateNonconformityDto,
  DictionaryItem,
  Nonconformity,
  NonconformityDictionaries,
  UpdateNonconformityDto,
} from './nonconformity.model';
import { mapToHttpParams } from '@bcl/design-system';
import {
  DictionaryItemV2,
  DictionaryService,
} from 'src/app/services/dictionary.service';

@Injectable({ providedIn: 'root' })
export class NonconformityApiService {
  constructor(
    private readonly http: HttpClient,
    private dictionaryService: DictionaryService
  ) {}

  public getNonconformity(id: number): Observable<Nonconformity> {
    return this.http.get<Nonconformity>(
      `${environment.apiHost}/nonconformity`,
      { params: { nonconformityId: id } }
    );
  }

  public createNonconformity(
    nonconformity: CreateNonconformityDto
  ): Observable<{ id: number }> {
    return this.http.post<{ id: number }>(
      `${environment.apiHost}/nonconformity`,
      nonconformity
    );
  }

  public updateNonconformity(
    nonconformity: UpdateNonconformityDto
  ): Observable<void> {
    return this.http
      .patch(`${environment.apiHost}/nonconformity`, nonconformity)
      .pipe(map(() => void 0));
  }

  public deleteNonconformity(id: number): Observable<void> {
    return this.http
      .delete(`${environment.apiHost}/nonconformity/${id}`)
      .pipe(map(() => void 0));
  }

  public getIncidentTypes(): Observable<DictionaryItem[]> {
    return this.dictionaryService
      .getDictionaryItemsByKeyV2('TABLE.NONCONFORMITY.TYPES')
      .pipe(map((response) => this.mapDictionaryV2ToItems(response)));
  }

  public getStatuses(): Observable<DictionaryItem[]> {
    return this.dictionaryService
      .getDictionaryItemsByKeyV2('TABLE.NONCONFORMITY.STATUSES')
      .pipe(map((response) => this.mapDictionaryV2ToItems(response)));
  }

  public getSeverities(): Observable<DictionaryItem[]> {
    return this.dictionaryService
      .getDictionaryItemsByKeyV2('TABLE.NC.INCIDENTSEVERITY')
      .pipe(map((response) => this.mapDictionaryV2ToItems(response)));
  }

  public getDivisions(): Observable<DictionaryItem[]> {
    return this.dictionaryService
      .getDictionaryItemsByKeyV2('TABLE.NONCONFORMITY.DIVISIONS')
      .pipe(map((response) => this.mapDictionaryV2ToItems(response)));
  }

  public getCategories(): Observable<DictionaryItem[]> {
    return this.dictionaryService
      .getDictionaryItemsByKeyV2('PC.NONCONFORMITIES.CATEGORIES')
      .pipe(map((response) => this.mapDictionaryV1ToItems(response)));
  }

  public getDictionaries(): Observable<NonconformityDictionaries> {
    return combineLatest([
      this.getIncidentTypes(),
      this.getStatuses(),
      this.getSeverities(),
      this.getDivisions(),
      this.getCategories(),
    ]).pipe(
      map(([incidentTypes, statuses, severities, divisions, categories]) => ({
        incidentTypes,
        statuses,
        severities,
        divisions,
        categories,
      }))
    );
  }

  private mapDictionaryV2ToItems(response: DictionaryItemV2): DictionaryItem[] {
    if (!response) {
      return [];
    }
    return response.Data.map((item) => ({
      value: item.Id?.toString() || '',
      name: item.Name?.toString() || '',
      additionalValues: undefined,
    }));
  }

  private mapDictionaryV1ToItems(response: DictionaryItemV2): DictionaryItem[] {
    if (!response) {
      return [];
    }
    return response.Data.map((item) => ({
      value: item.Value || '',
      name: item.Name?.toString() || '',
      additionalValues: undefined,
    }));
  }
}
